import React, { useEffect, useState } from "react";
import { useSearchParams, useParams, Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Truck, GraduationCap, Sparkles, ArrowRight, Monitor, PaintBucket } from "lucide-react";
import { Reveal } from "./components";
import { QuoteCTA } from "./pages";
import { MEDIA } from "./data";
import { useCart } from "./CartContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProductGrid({ products, icon }) {
  const { add } = useCart();
  return (
    <div className="grid md:grid-cols-3 gap-8 mt-10">
      {products.map((p, i) => (
        <Reveal key={p.id} delay={i * 0.08}>
          <div data-testid={`product-${p.id}`} className="group border border-black/10 bg-white flex flex-col h-full hover:shadow-[0_24px_50px_-20px_rgba(0,0,0,0.15)] transition-shadow duration-500">
            <div className="overflow-hidden relative h-56">
              {Array.isArray(p.swatches) && p.swatches.length > 0 ? (
                <div data-testid={`swatch-strip-${p.id}`} className="w-full h-full flex">
                  {p.swatches.map((hex, sIdx) => (
                    <div key={`${hex}-${sIdx}`} className="flex-1 transition-transform duration-700 group-hover:scale-[1.02]" style={{ background: hex }} />
                  ))}
                </div>
              ) : (
                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              )}
              {p.badge && <span data-testid={`badge-${p.id}`} className="absolute top-3 right-3 bg-gold text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">{p.badge}</span>}
            </div>
            <div className="p-7 flex flex-col flex-1">
              <div className="flex items-center gap-2 text-gold overline">{icon}{p.type === "course" ? "Self-paced online course" : p.type === "sample" ? `${p.count} ${p.count === 1 ? "color" : "colors"}` : p.turnaround}</div>
              <h3 className="font-serif text-2xl mt-2">{p.name}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.description}</p>
              {Array.isArray(p.includes) && p.includes.length > 0 && (
                <ul data-testid={`includes-${p.id}`} className="mt-4 space-y-1.5">
                  {p.includes.map((line) => (
                    <li key={line} className="flex items-start gap-2 text-xs text-ink/80"><Check size={13} className="text-gold mt-0.5 shrink-0" /> <span>{line}</span></li>
                  ))}
                </ul>
              )}
              {p.addon_note && <p data-testid={`addon-note-${p.id}`} className="mt-3 text-xs italic text-ink/60">{p.addon_note}</p>}
              <div className="mt-auto pt-6">
                <span className="font-serif text-3xl block">${p.price.toFixed(2)}</span>
                <Button onClick={() => { add(p); toast.success(`${p.name} added to cart`); }} data-testid={`add-${p.id}`} className="bg-ink text-white rounded-none hover:bg-gold overline w-full mt-4 py-5">Add to Cart</Button>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export function ShopCategory({ type, title, subtitle }) {
  const [products, setProducts] = useState(null); // null = loading, [] = loaded/empty, array = loaded
  const [error, setError] = useState(false);
  useEffect(() => {
    setProducts(null); setError(false);
    axios.get(`${API}/products?type=${type}`)
      .then((r) => setProducts(r.data))
      .catch((e) => { console.error("Failed to load products", e); setError(true); setProducts([]); });
  }, [type]);
  const icon = type === "course" ? <GraduationCap size={13} /> : type === "sample" ? <Truck size={13} /> : <Sparkles size={13} />;
  return (
    <div data-testid={`${type}-page`} className="pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h1 className="font-serif text-5xl md:text-6xl">{title}</h1>
        <p className="eyebrow text-gold mt-4">The Shop</p>
        <p className="text-muted-foreground mt-5 max-w-2xl leading-relaxed">{subtitle}</p>
        {products === null && (
          <div className="flex items-center gap-3 mt-16 text-ink/60" data-testid="products-loading"><Loader2 className="animate-spin text-gold" size={18}/> Loading…</div>
        )}
        {error && (
          <div className="mt-16 border border-red-200 bg-red-50 text-red-700 px-6 py-5" data-testid="products-error">
            <p className="font-serif text-lg">We couldn't load these packages.</p>
            <p className="text-sm mt-1">Please refresh the page. If the issue persists, <Link to="/contact" className="underline">contact us</Link>.</p>
          </div>
        )}
        {products !== null && !error && <ProductGrid products={products} icon={icon} />}
        <Link to="/shop" className="inline-flex items-center gap-2 overline text-ink mt-12 hover:text-gold transition-colors">Browse the full shop &rarr;</Link>
      </div>
      <QuoteCTA />
    </div>
  );
}

export function Shop() {
  const [products, setProducts] = useState([]);
  useEffect(() => { axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {}); }, []);
  const courses = products.filter((p) => p.type === "course");
  const renderings = products.filter((p) => p.type === "rendering");

  return (
    <div data-testid="shop-page" className="pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h1 className="font-serif text-5xl md:text-6xl">Courses &amp; Renderings</h1>
        <p className="eyebrow text-gold mt-4">The Shop</p>
        <p className="text-muted-foreground mt-5 max-w-2xl">Learn to choose color like a designer and see your home rendered in your final palette before you paint.</p>
        <Tabs defaultValue="courses" className="mt-12">
          <TabsList className="bg-white border border-black/10 rounded-none p-1 flex-wrap h-auto">
            <TabsTrigger value="courses" data-testid="tab-courses" className="rounded-none overline data-[state=active]:bg-ink data-[state=active]:text-white">Color Courses</TabsTrigger>
            <TabsTrigger value="renderings" data-testid="tab-renderings" className="rounded-none overline data-[state=active]:bg-ink data-[state=active]:text-white">Renderings</TabsTrigger>
          </TabsList>
          <TabsContent value="courses"><ProductGrid products={courses} icon={<GraduationCap size={13} />} /></TabsContent>
          <TabsContent value="renderings"><ProductGrid products={renderings} icon={<Sparkles size={13} />} /></TabsContent>
        </Tabs>
      </div>
      <QuoteCTA />
    </div>
  );
}

export function CheckoutSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const { clear } = useCart();
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (r.data.status === "expired") { setStatus("error"); return; }
      } catch { /* retry */ }
      if (attempts++ < 6) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="success-page" className="pt-32 min-h-screen">
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        {status === "checking" && <><Loader2 className="animate-spin mx-auto text-gold" size={40} /><p className="mt-6 font-serif text-3xl">Confirming your payment…</p></>}
        {status === "paid" && <><div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto"><Check className="text-white" size={32} /></div><h1 className="font-serif text-5xl mt-6">Thank You</h1><p className="text-muted-foreground mt-4">Your order is confirmed. A receipt is on its way. Courses are available immediately and renderings are delivered within your quoted turnaround.</p><Link to="/shop" className="inline-block mt-8 bg-ink text-white overline px-8 py-4 hover:bg-gold transition-colors">Continue Shopping</Link></>}
        {(status === "error" || status === "timeout") && <><h1 className="font-serif text-4xl">Payment status unavailable</h1><p className="text-muted-foreground mt-4">If you completed payment, please check your email for confirmation.</p><Link to="/shop" className="inline-block mt-8 border border-ink overline px-8 py-4">Back to Shop</Link></>}
      </div>
    </div>
  );
}

export function Admin() {
  const [pass, setPass] = useState(localStorage.getItem("bhpc_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [orders, setOrders] = useState([]);
  const location = useLocation();
  const initialTab = location.pathname.endsWith("/referrals") ? "referrals" : "leads";

  const load = async (code) => {
    try {
      const h = { headers: { "X-Admin-Passcode": code } };
      const [l, o] = await Promise.all([axios.get(`${API}/admin/leads`, h), axios.get(`${API}/admin/orders`, h)]);
      setLeads(l.data); setOrders(o.data); setAuthed(true);
      localStorage.setItem("bhpc_admin", code);
    } catch { toast.error("Invalid passcode"); setAuthed(false); }
  };

  useEffect(() => { if (pass) load(pass); }, []);

  if (!authed) {
    return (
      <div data-testid="admin-login" className="pt-32 min-h-screen max-w-md mx-auto px-6 py-24">
        <h1 className="font-serif text-4xl">Admin</h1>
        <input type="password" placeholder="Passcode" value={pass} onChange={(e) => setPass(e.target.value)} data-testid="admin-pass" className="w-full border border-black/20 px-4 py-3 bg-white mt-6 text-sm" />
        <Button onClick={() => load(pass)} data-testid="admin-login-btn" className="w-full bg-ink text-white rounded-none hover:bg-gold py-6 overline mt-3">Enter</Button>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="pt-32 max-w-7xl mx-auto px-6 md:px-12 py-16">
      <h1 className="font-serif text-5xl">Dashboard</h1>
      <Tabs defaultValue={initialTab} className="mt-8">
        <TabsList className="bg-white border border-black/10 rounded-none flex-wrap h-auto">
          <TabsTrigger value="leads" className="rounded-none overline data-[state=active]:bg-ink data-[state=active]:text-white">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-none overline data-[state=active]:bg-ink data-[state=active]:text-white">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="admin-tab-referrals" className="rounded-none overline data-[state=active]:bg-ink data-[state=active]:text-white">Referrals</TabsTrigger>
          <TabsTrigger value="colors" data-testid="admin-tab-colors" className="rounded-none overline data-[state=active]:bg-ink data-[state=active]:text-white">Colors</TabsTrigger>
        </TabsList>
        <TabsContent value="leads">
          <Table data-testid="leads-table"><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Project</TableHead><TableHead>Area</TableHead><TableHead>Message</TableHead></TableRow></TableHeader>
            <TableBody>{leads.map((l) => <TableRow key={l.id}><TableCell>{l.name}</TableCell><TableCell>{l.email}</TableCell><TableCell>{l.phone}</TableCell><TableCell>{l.project_type}</TableCell><TableCell>{l.service_area}</TableCell><TableCell className="max-w-xs truncate">{l.message}</TableCell></TableRow>)}</TableBody>
          </Table>
        </TabsContent>
        <TabsContent value="orders">
          <Table data-testid="orders-table"><TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Email</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead></TableRow></TableHeader>
            <TableBody>{orders.map((o) => <TableRow key={o.id}><TableCell>{o.customer_name}</TableCell><TableCell>{o.customer_email}</TableCell><TableCell>{o.items.map((i) => `${i.name} x${i.quantity}`).join(", ")}</TableCell><TableCell>${o.amount.toFixed(2)}</TableCell></TableRow>)}</TableBody>
          </Table>
        </TabsContent>
        <TabsContent value="referrals">
          <AdminReferrals passcode={pass} />
        </TabsContent>
        <TabsContent value="colors">
          <AdminColors passcode={pass} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AdminReferrals({ passcode }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [notes, setNotes] = useState({}); // { [id]: string }
  const h = { headers: { "X-Admin-Passcode": passcode } };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/referrals`, h);
      setRows(r.data);
    } catch { toast.error("Could not load referrals"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/referrals/${id}`, patch, h);
      toast.success("Referral updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const STATUSES = [
    { v: "submitted", label: "Submitted" },
    { v: "contacted", label: "Contacted" },
    { v: "consult_booked", label: "Consult Booked" },
    { v: "job_booked", label: "Job Booked" },
    { v: "paid_out", label: "Paid Out" },
    { v: "closed", label: "Closed" },
  ];
  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);
  const rewardTier = (r) => r.referrer_referral_index === 1
    ? "1st — free consult + $150"
    : `#${r.referrer_referral_index} — $150`;

  return (
    <div className="mt-6 space-y-6" data-testid="admin-referrals-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilter("all")} className={`overline px-3 py-1.5 border ${filter==="all"?"bg-ink text-white border-ink":"border-black/15 text-ink hover:border-gold"}`} data-testid="filter-all">All ({rows.length})</button>
          {STATUSES.map((s) => (
            <button key={s.v} onClick={() => setFilter(s.v)} className={`overline px-3 py-1.5 border ${filter===s.v?"bg-ink text-white border-ink":"border-black/15 text-ink hover:border-gold"}`} data-testid={`filter-${s.v}`}>
              {s.label} ({rows.filter((r)=>r.status===s.v).length})
            </button>
          ))}
        </div>
        <button onClick={load} className="text-sm text-ink/60 hover:text-gold underline underline-offset-4" data-testid="refresh-referrals">Refresh</button>
      </div>
      {loading ? (
        <p className="text-sm text-ink/60">Loading referrals…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-ink/60 py-6">No referrals in this bucket yet.</p>
      ) : (
        <div className="space-y-4">
          {shown.map((r) => (
            <div key={r.id} className="bg-white border border-black/10 p-5" data-testid={`admin-referral-${r.id}`}>
              <div className="grid md:grid-cols-[1.4fr_1.4fr_1fr] gap-6">
                <div>
                  <p className="overline text-[10px] text-ink/60 mb-1">Referrer</p>
                  <p className="font-serif text-lg">{r.referrer_name}</p>
                  <p className="text-xs text-ink/70">{r.referrer_email}</p>
                  <p className="text-xs text-ink/70">{r.referrer_phone || "—"}</p>
                  <p className="text-[11px] text-gold mt-2">{rewardTier(r)}</p>
                </div>
                <div>
                  <p className="overline text-[10px] text-ink/60 mb-1">Friend</p>
                  <p className="font-serif text-lg">{r.friend_name}</p>
                  <p className="text-xs text-ink/70">{[r.friend_city, r.friend_email, r.friend_phone].filter(Boolean).join(" · ") || "—"}</p>
                  {r.project_notes && <p className="text-xs text-ink/60 mt-2 leading-relaxed">{r.project_notes}</p>}
                  <p className="text-[10px] text-ink/50 mt-3">Submitted {new Date(r.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="overline text-[10px] text-ink/60 mb-1">Status</p>
                  <select
                    value={r.status}
                    onChange={(e) => update(r.id, { status: e.target.value, admin_notes: r.admin_notes || null })}
                    data-testid={`status-select-${r.id}`}
                    className="w-full border border-black/15 focus:border-gold px-3 py-2 text-sm bg-white outline-none"
                  >
                    {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                  <p className="overline text-[10px] text-ink/60 mt-4 mb-1">Admin notes</p>
                  <textarea
                    rows={2}
                    defaultValue={r.admin_notes || ""}
                    onBlur={(e) => { if (e.target.value !== (r.admin_notes || "")) update(r.id, { status: r.status, admin_notes: e.target.value }); }}
                    onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                    data-testid={`notes-${r.id}`}
                    className="w-full border border-black/15 focus:border-gold px-3 py-2 text-xs bg-white outline-none"
                    placeholder="Private notes (saves on blur)…"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminColors({ passcode }) {
  const h = { headers: { "X-Admin-Passcode": passcode } };
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [form, setForm] = useState({ brand: "Sherwin-Williams", code: "", name: "", hex: "" });
  const [importText, setImportText] = useState("");
  const LIMIT = 25;

  const loadStats = () => axios.get(`${API}/admin/colors/stats`, h).then((r) => setStats(r.data)).catch(() => {});
  const loadColors = async () => {
    const p = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
    if (q) p.set("q", q);
    if (brand) p.set("brand", brand);
    const r = await axios.get(`${API}/admin/colors?${p.toString()}`, h);
    setItems(r.data.items); setTotal(r.data.total);
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { const t = setTimeout(loadColors, 250); return () => clearTimeout(t); }, [q, brand, offset]);

  const patch = async (c, changes) => {
    await axios.put(`${API}/admin/colors/${c.id}`, changes, h);
    loadColors(); loadStats();
  };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name} (${c.code})?`)) return;
    await axios.delete(`${API}/admin/colors/${c.id}`, h);
    loadColors(); loadStats();
  };
  const addColor = async () => {
    try {
      await axios.post(`${API}/admin/colors`, form, h);
      toast.success("Color added"); setForm({ brand: form.brand, code: "", name: "", hex: "" });
      loadColors(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add color"); }
  };
  const runImport = async () => {
    try {
      const r = await axios.post(`${API}/admin/colors/import`, importText, { headers: { ...h.headers, "Content-Type": "text/plain" } });
      toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated, ${r.data.skipped} skipped`);
      setImportText(""); loadColors(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  };
  const exportData = async (fmt) => {
    const r = await axios.get(`${API}/admin/colors/export?fmt=${fmt}`, { ...h, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a"); a.href = url; a.download = `bhpc-colors.${fmt}`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-colors" className="mt-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[["Total", stats.total], ["Active", stats.active], ["Featured", stats.featured], ["Excluded", stats.excluded], ["Brands", Object.keys(stats.by_brand).length]].map(([k, v]) => (
            <div key={k} className="border border-black/10 bg-white p-4"><p className="text-xs uppercase tracking-widest text-ink/50">{k}</p><p data-testid={`stat-${k.toLowerCase()}`} className="font-serif text-2xl mt-1">{v.toLocaleString()}</p></div>
          ))}
        </div>
      )}

      {/* Add + Import + Export */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="border border-black/10 bg-white p-5">
          <p className="overline text-gold">Add a color</p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <select value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="add-brand" className="border border-black/15 px-3 py-2 text-sm col-span-2">
              <option>Sherwin-Williams</option><option>Benjamin Moore</option>
            </select>
            <input placeholder="Code (e.g. SW 7029)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="add-code" className="border border-black/15 px-3 py-2 text-sm" />
            <input placeholder="Hex (#RRGGBB)" value={form.hex} onChange={(e) => setForm({ ...form, hex: e.target.value })} data-testid="add-hex" className="border border-black/15 px-3 py-2 text-sm" />
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-name" className="border border-black/15 px-3 py-2 text-sm col-span-2" />
          </div>
          <Button onClick={addColor} data-testid="add-color-btn" className="bg-ink text-white rounded-none hover:bg-gold overline mt-3 py-4 w-full">Add Color</Button>
        </div>
        <div className="border border-black/10 bg-white p-5">
          <p className="overline text-gold">Bulk import (CSV or JSON)</p>
          <textarea value={importText} onChange={(e) => setImportText(e.target.value)} data-testid="import-textarea" placeholder='CSV header: brand,code,name,hex[,family,featured,active]  — or paste a JSON array' className="w-full h-24 border border-black/15 px-3 py-2 text-xs font-mono mt-3" />
          <div className="flex flex-wrap gap-2 mt-3">
            <Button onClick={runImport} data-testid="import-btn" className="bg-ink text-white rounded-none hover:bg-gold overline py-4">Import</Button>
            <button onClick={() => exportData("csv")} data-testid="export-csv" className="border border-ink overline px-5 py-2 text-xs hover:bg-ink hover:text-white transition-colors">Export CSV</button>
            <button onClick={() => exportData("json")} data-testid="export-json" className="border border-ink overline px-5 py-2 text-xs hover:bg-ink hover:text-white transition-colors">Export JSON</button>
          </div>
        </div>
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-2 mb-3">
        <input value={q} onChange={(e) => { setOffset(0); setQ(e.target.value); }} data-testid="admin-color-search" placeholder="Search name or code…" className="flex-1 min-w-[200px] border border-black/15 px-4 py-2.5 text-sm" />
        <select value={brand} onChange={(e) => { setOffset(0); setBrand(e.target.value); }} data-testid="admin-color-brand" className="border border-black/15 px-3 py-2.5 text-sm">
          <option value="">All Brands</option><option>Sherwin-Williams</option><option>Benjamin Moore</option>
        </select>
      </div>

      <div className="overflow-x-auto border border-black/10 bg-white">
        <Table data-testid="admin-colors-table">
          <TableHeader><TableRow><TableHead>Swatch</TableHead><TableHead>Brand</TableHead><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Family</TableHead><TableHead>Undertone</TableHead><TableHead>Flags</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`admin-color-row-${c.code.replace(/\s/g, "")}`}>
                <TableCell><span className="inline-block w-8 h-8 rounded border border-black/10" style={{ background: c.hex }} /></TableCell>
                <TableCell className="text-xs">{c.brand.split("-")[0].split(" ")[0]}</TableCell>
                <TableCell className="font-mono text-xs">{c.code}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell className="text-xs">{c.family}</TableCell>
                <TableCell className="text-xs">{c.undertone}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button onClick={() => patch(c, { featured: !c.featured })} data-testid={`toggle-featured-${c.code.replace(/\s/g, "")}`} className={`text-[10px] px-2 py-1 border ${c.featured ? "bg-gold text-white border-gold" : "border-black/20"}`}>Feature</button>
                    <button onClick={() => patch(c, { exclude_from_quiz: !c.exclude_from_quiz })} data-testid={`toggle-exclude-${c.code.replace(/\s/g, "")}`} className={`text-[10px] px-2 py-1 border ${c.exclude_from_quiz ? "bg-ink text-white border-ink" : "border-black/20"}`}>Exclude</button>
                    <button onClick={() => patch(c, { active: !c.active })} className={`text-[10px] px-2 py-1 border ${c.active ? "border-black/20" : "bg-red-600 text-white border-red-600"}`}>{c.active ? "Active" : "Off"}</button>
                  </div>
                </TableCell>
                <TableCell><button onClick={() => del(c)} data-testid={`delete-${c.code.replace(/\s/g, "")}`} className="text-xs text-red-600 hover:underline">Delete</button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <p className="text-sm text-ink/50">{total.toLocaleString()} colors · showing {offset + 1}–{Math.min(offset + LIMIT, total)}</p>
        <div className="flex gap-2">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} className="border border-ink overline px-4 py-2 text-xs disabled:opacity-30">Prev</button>
          <button disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)} data-testid="colors-next-page" className="border border-ink overline px-4 py-2 text-xs disabled:opacity-30">Next</button>
        </div>
      </div>
    </div>
  );
}
